// filepath: apps/web/src/api/singleFlight.ts
//
// ISS-377 (uat-ba note_13124) NON-BLOCKING-2 — single-flight
// coalescer for `auth.signinSilent()` calls.
//
// The OIDC library's `signinSilent()` is internally idempotent
// (`oidc-client-ts` deduplicates in-flight refresh calls), but the
// web `apiFetch` client invokes it independently from each
// concurrent 401. Two simultaneous 401s from two apiFetch callers
// therefore produce two distinct `signinSilent()` call sites that
// race against each other; the second one is a wasted network
// round-trip and (depending on library version) can land on a
// stale refresh token. The mobile equivalent
// (ISS-f4fa1b7b-391, `DioApiClient`) wraps its refresh call in an
// explicit single-flight; the web client now mirrors that pattern.
//
// Mechanism:
//
//   - The module-scoped `inflight` slot holds the in-flight
//     promise from the most recent `signinSilentRefresh()` call.
//   - Concurrent callers `await` the same promise — they do NOT
//     call `signinSilent()` themselves.
//   - On rejection the slot is cleared so the next 401 starts a
//     fresh refresh. This is the same shape the mobile pattern
//     uses (`asMaybeLike` from DioApiClient).
//   - The slot is keyed ONLY by promise (not by caller or URL) —
//     every concurrent caller wants the same refreshed token.
//   - The exported helper wraps `auth.signinSilent()` and returns
//     the resolved value (typically `User | null` from
//     `oidc-client-ts`). The round-2.1 R2-B1 fix requires the
//     resolved value so the replay can attach the freshly-minted
//     bearer (the React render-time accessor ref is stale at the
//     microtask of `signinSilent()`'s resolution).
//
// The slot lives at module level (not a per-call closure) so two
// `apiFetch()` invocations that race against the same OIDC refresh
// coalesce. The slot is cleared on both success and rejection (a
// stale completed promise is benign but a leak; clear
// unconditionally to keep the slot clean).

/**
 * In-flight `signinSilent()` promise. `null` when no refresh is
 * in progress. Cleared on rejection so the next 401 starts fresh.
 */
let inflight: Promise<unknown> | null = null;

/**
 * Run `auth.signinSilent()` through a single-flight coalescer.
 *
 * Concurrent callers `await` the same refresh — only the first
 * caller invokes `signinSilent()`; the others wait on the same
 * promise. On rejection, the slot is cleared so the next 401
 * retries fresh; on success the resolved Promise resolves for
 * every caller.
 *
 * The `signinSilent` argument is the function from
 * `AuthContextProps`. The caller is responsible for passing the
 * current `auth` instance (the apiFetch handler reads it off the
 * discriminated auth strategy). The OIDC library's
 * `signinSilent()` returns `Promise<User | null>`; we coerce to
 * `Promise<void>` here because the apiFetch 401 handler only
 * observes success/reject via try/catch and never reads the
 * resolved user — `useAuth()` is re-read on the next render.
 */
export function signinSilentRefresh(
  signinSilent: () => Promise<unknown>,
): Promise<unknown> {
  if (inflight !== null) {
    return inflight;
  }
  // ISS-377 round 2.1 (R2-B1): the resolved value (the
  // freshly-minted `User` from `oidc-client-ts`) is propagated
  // to the caller. The previous shape coerced to `Promise<void>`
  // because the apiFetch 401 handler only observed success/reject
  // via try/catch and never read the resolved user — `useAuth()`
  // was re-read on the next render. The round-2.1 R2-B1 fix
  // exposes the resolved User so the replay branch can attach
  // the fresh token WITHOUT re-reading the React render-time
  // accessor (which is stale at the microtask of
  // `signinSilent()`'s resolution).
  inflight = signinSilent()
    .then(
      (value: unknown) => value,
      (err: unknown) => {
        throw err;
      },
    )
    .finally(() => {
      // Clear on rejection so the next 401 starts a fresh refresh.
      // On success we ALSO clear (a stale completed promise is
      // benign but a leak; clear unconditionally to keep the slot
      // clean). Note: `finally` runs on both success and rejection,
      // so this is a single clear-point.
      inflight = null;
    });
  return inflight;
}

/**
 * Test-only escape hatch. Resets the single-flight slot.
 * Mirrors `__resetMutationReplayGuardForTests` /
 * `__resetRuntimeConfigForTests` so per-test setup does not
 * leak. Production code MUST NOT call this — the slot's
 * lifecycle is owned by `signinSilentRefresh()`.
 */
export function __resetSingleFlightForTests(): void {
  inflight = null;
}
